import React, { useEffect } from 'react';
import { Spinner } from '../../components/UI';
import {
    Button,
    Paper,
    Box,
    Typography,
    TablePagination,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableBody,
    Divider,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Chip,
} from '@material-ui/core';
import HorizontalScrollableFilter from '../../components/UI/HorizontalScrollableFilter';
import ClearIcon from '@material-ui/icons/Clear';
import RefreshIcon from '@material-ui/icons/Refresh';
import TableSearchBar from '../../components/TableSearchBar';
import useRiderManagement from './useRiderManagement';
import {
    RIDER_MANAGEMENT_TABLE_COLUMNS,
    EMP_ID_COLUMN,
    LAST_WORKING_DATE_COLUMN,
} from './rider.helper';
import { RiderTableCell, RiderTableHeadCell } from './utility.component.rider';
import AddEditStaffModal from '../../components/Modals/AddEditStaffModal';
import RiderViewModal from '../../components/Modals/RiderViewModal';
import RiderDeactivationModal from '../../components/Modals/RiderDeactivationModal';
import ResetMpinModal from '../../components/Modals/ResetMpinModal';
import { isBdDeactivationEnabled } from '../../utils/appHelper';
import clsx from 'clsx';
import FilteredStateV2 from '../../components/FilteredStateV2';
import FilterSelectModal from '../../components/FilterSelectModal';
import UploadPayEarnModal from '../../components/Modals/UploadPayEarnModal';
import { SelectCheckBox } from '../hire';
import { showMessage } from '../../utils/helper';

interface RiderManagementPageProps {
    externalFiltered?: any;
    renderHubFlow?: boolean;
    [key: string]: any;
}

const RiderManagementPage: React.FC<RiderManagementPageProps> = ({
    externalFiltered,
    ...props
}) => {
    const {
        handleRowClick,
        getColumnComponentValue,
        prepareFilterCard,
        riderList,
        pagination,
        handleChangeRowsPerPage,
        onSearchTextChange,
        activeFilterCard,
        searchTextValue,
        permissions,
        loading,
        containerRef,
        styles,
        handleChangePage,
        closeRiderModal,
        selectedRider,
        isModalOpen,
        handleFilterCardClick,
        RIDER_FILTER_CARDS,
        filterValues,
        fetchRiderCounts,
        fetchRidersData,
        openBulkUploadModal,
        closeBulkUploadModal,
        bulkUploadType,
        openBulkModal,
        selected,
        handleAllSelect,
        handleSelect,
        isViewModalOpen,
        mpinRider,
        setMpinRider,
        closeViewModal,
        deactivateConfirmRider,
        reactivateConfirmRider,
        setReactivateConfirmRider,
        handleConfirmReactivate,
        setDeactivateConfirmRider,
        handleConfirmDeactivate,
        // toolbar filters + search
        toolbarFilterOptions,
        scopeBadges,
        hasNoScopeAssigned,
        handleFilterChange,
        handleRemoveFilter,
        handleRemoveFilterValue,
        handleClearAllFilters,
        activeFilterCount,
        handleSearchSubmit,
        handleSearchClear,
        isSearchDirty,
        appliedSearchText,
    } = useRiderManagement({ externalFiltered, ...props });
    const uploadUIData: any = {
        emp_id_mapping: {
            title: 'Upload Employee ID Mapping',
            type: 'emp_id_mapping',
            enumType: 'emp_id_mapping',
            buttonText: 'Upload Employee ID Mapping',
        },
    };
    const [allSelected, setAllSelected] = React.useState(false);
    useEffect(() => {
        if (selected.length === 0) {
            setAllSelected(false);
        }
    }, [selected]);

    const displayColumns = React.useMemo(() => {
        // One extra column per view, each only where it has a value to show:
        // an employee ID on the mapped view, a final day on the deactivated one.
        let extraColumn: any = null;
        if (activeFilterCard?.filterStatus === 'empIdMapped') {
            extraColumn = EMP_ID_COLUMN;
        } else if (activeFilterCard?.filterStatus === 'deactivated') {
            extraColumn = LAST_WORKING_DATE_COLUMN;
        }
        if (!extraColumn) {
            return RIDER_MANAGEMENT_TABLE_COLUMNS;
        }
        // It slots in just before Actions, which is always the trailing
        // column — located by key rather than by index so the insert can't
        // drift if the column order changes again.
        const cols = [...RIDER_MANAGEMENT_TABLE_COLUMNS];
        const actionIndex = cols.findIndex(
            (col: any) => col?.mappingKey === 'action',
        );
        cols.splice(
            actionIndex === -1 ? cols.length : actionIndex,
            0,
            extraColumn,
        );
        return cols;
    }, [activeFilterCard?.filterStatus]);

    // The table fills its container and the space beyond the sum of the column
    // bases is split equally between them, so wide values (vendor names, cities)
    // use the room instead of truncating while it sits empty on the right.
    // `fixedWidth` columns opt out — their content can't grow, so a share would
    // only read as a gap. Below the total, the bases hold and the container
    // scrolls horizontally.
    const columnWidth = React.useMemo(() => {
        const totalBase = displayColumns.reduce(
            (sum: number, col: any) => sum + (col?.width ?? 0),
            0,
        );
        const growable =
            displayColumns.filter((col: any) => !col?.fixedWidth).length || 1;
        return {
            totalBase,
            styleFor: (col: any) =>
                col?.fixedWidth
                    ? { width: col?.width }
                    : {
                          width: `calc(${col?.width ?? 0}px + (100% - ${totalBase}px) / ${growable})`,
                      },
        };
    }, [displayColumns]);

    const hasRows = !!riderList?.lineItems?.length;

    return (
        <Spinner loading={loading} isBackDrop>
            <Box>
                <Box mt={2} className={styles.sparkLineContainer}>
                    <HorizontalScrollableFilter scrollAmount={250} gap={12}>
                        {RIDER_FILTER_CARDS?.map((card: any, index: number) => {
                            const isActive =
                                card?.type === activeFilterCard?.filterStatus;
                            return (
                                <Box
                                    key={index}
                                    className={clsx(styles.filterBox, {
                                        [styles.filterBoxActive]: isActive,
                                    })}
                                    style={{ flexShrink: 0 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleFilterCardClick(card);
                                    }}
                                >
                                    {prepareFilterCard(card)}
                                </Box>
                            );
                        })}
                    </HorizontalScrollableFilter>
                </Box>

                {/* Toolbar: explicit search submit, then the filter pickers. */}
                <Box mt={2} mb={1} className={styles.toolbar}>
                    <TableSearchBar
                        placeholder="Search By Phone"
                        value={searchTextValue}
                        onChange={onSearchTextChange}
                        onSearch={handleSearchSubmit}
                        onClear={handleSearchClear}
                        isDirty={isSearchDirty}
                        hasApplied={!!appliedSearchText}
                        width={300}
                    />

                    <Box className={styles.toolbarDivider} />

                    {/* A filter narrowed to exactly one option is the caller's scope, not a
                        choice — shown as a read-only badge so they can still see what it is. */}
                    {scopeBadges?.map((badge: any) => (
                        <Chip
                            key={badge?.name}
                            size="small"
                            label={`${badge?.label}: ${badge?.value}`}
                            className={styles.scopeBadge}
                        />
                    ))}

                    {toolbarFilterOptions?.length > 0 && (
                        <Typography className={styles.filterLabel}>
                            Filters
                        </Typography>
                    )}
                    {toolbarFilterOptions?.map((opt: any) => (
                        <FilterSelectModal
                            key={opt?.name}
                            label={opt?.label}
                            options={opt?.options ?? []}
                            value={opt?.value ?? []}
                            onChange={(selectedOptions: any) =>
                                handleFilterChange(opt?.name, selectedOptions)
                            }
                        />
                    ))}
                    {activeFilterCount > 0 && (
                        <Button
                            className={styles.ghostButton}
                            startIcon={<ClearIcon fontSize="small" />}
                            onClick={handleClearAllFilters}
                        >
                            {`Clear filters (${activeFilterCount})`}
                        </Button>
                    )}

                    <Box
                        ml="auto"
                        display="flex"
                        alignItems="center"
                        gridGap={8}
                    >
                        {hasRows && (
                            <Typography className={styles.resultSummary}>
                                {`${riderList?.totalCount ?? 0} riders`}
                            </Typography>
                        )}
                        <Tooltip title="Refresh" arrow>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    fetchRiderCounts?.();
                                    fetchRidersData?.();
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {!!permissions?.update &&
                    activeFilterCard?.filterStatus ===
                        'pendingOpsVerification' &&
                    selected?.length > 0 && (
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            mb={1}
                            style={{ gap: 8 }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    openBulkUploadModal('emp_id_mapping');
                                }}
                                disabled={!permissions.create}
                                disableElevation
                            >
                                {`Accept ${selected?.length} selected`}
                            </Button>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    openBulkUploadModal('emp_id_mapping');
                                }}
                                disabled={!permissions.create}
                                disableElevation
                            >
                                Reject selected
                            </Button>
                        </Box>
                    )}
                {!!openBulkModal && !!bulkUploadType && (
                    <UploadPayEarnModal
                        open={openBulkModal}
                        data={{}}
                        isEdit={false}
                        enumType={bulkUploadType}
                        onClose={() => {
                            closeBulkUploadModal?.();
                        }}
                        uiData={
                            !!bulkUploadType
                                ? uploadUIData?.[bulkUploadType]
                                : undefined
                        }
                    />
                )}

                <FilteredStateV2
                    filterOptions={toolbarFilterOptions}
                    filteredValues={filterValues}
                    onRemoveValue={handleRemoveFilterValue}
                    onRemoveGroup={handleRemoveFilter}
                    onClearAll={handleClearAllFilters}
                    inlineLimit={3}
                />
                <Paper
                    ref={containerRef}
                    className={styles.container}
                    elevation={0}
                    style={{ padding: '1rem' }}
                >
                    {hasRows ? (
                        <>
                            <TableContainer
                                component={Box}
                                className={styles.tableContainer}
                            >
                                <Table
                                    stickyHeader
                                    aria-label="Riders table"
                                    style={{
                                        tableLayout: 'fixed',
                                        minWidth: columnWidth.totalBase,
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {/* {permissions &&
                                            permissions.create &&
                                            activeFilterCard &&
                                            activeFilterCard.filterStatus &&
                                            activeFilterCard.filterStatus ===
                                                'pendingOpsVerification' && (
                                                <RiderTableHeadCell
                                                    key={`column`}
                                                >
                                                    <SelectCheckBox
                                                        disabled={false}
                                                        checked={allSelected}
                                                        onChange={(e: any) => {
                                                            if (
                                                                selected.length <=
                                                                50
                                                            ) {
                                                                setAllSelected(
                                                                    e.target
                                                                        .checked,
                                                                );
                                                                if (
                                                                    handleAllSelect
                                                                ) {
                                                                    handleAllSelect(
                                                                        e.target
                                                                            .checked,
                                                                        riderList?.lineItems,
                                                                    );
                                                                }
                                                            } else {
                                                                showMessage(
                                                                    'Maximum Selection Limit Reached',
                                                                    'error',
                                                                );
                                                            }
                                                        }}
                                                        indeterminate={
                                                            allSelected
                                                        }
                                                    />
                                                </RiderTableHeadCell>
                                            )} */}
                                            {displayColumns?.map(
                                                (
                                                    column: any,
                                                    index: number,
                                                ) => {
                                                    return (
                                                        <RiderTableHeadCell
                                                            key={`column${index}`}
                                                            align={
                                                                column?.align ??
                                                                'left'
                                                            }
                                                            style={columnWidth.styleFor(
                                                                column,
                                                            )}
                                                        >
                                                            {column?.name}
                                                        </RiderTableHeadCell>
                                                    );
                                                },
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {riderList?.lineItems?.map(
                                            (row: any, rowIndex: number) => (
                                                <React.Fragment
                                                    key={row.id || rowIndex}
                                                >
                                                    <TableRow
                                                        className={
                                                            styles.tableRow
                                                        }
                                                        onClick={(e: any) => {
                                                            handleRowClick(
                                                                e,
                                                                row,
                                                            );
                                                        }}
                                                    >
                                                        {/* {permissions &&
                                                        permissions.create &&
                                                        activeFilterCard &&
                                                        activeFilterCard?.filterStatus &&
                                                        activeFilterCard?.filterStatus ===
                                                            'pendingOpsVerification' && (
                                                            <RiderTableCell
                                                                key={`row-checkox`}
                                                            >
                                                                <SelectCheckBox
                                                                    checked={
                                                                        selected.indexOf(
                                                                            row?.id,
                                                                        ) !== -1
                                                                    }
                                                                    onChange={(
                                                                        e: any,
                                                                    ) => {
                                                                        e?.stopPropagation();
                                                                        e?.preventDefault();
                                                                        handleSelect(
                                                                            row,
                                                                        );
                                                                    }}
                                                                    disabled={
                                                                        false
                                                                    }
                                                                    key={
                                                                        row?.id
                                                                    }
                                                                />
                                                            </RiderTableCell>
                                                        )} */}
                                                        {displayColumns?.map(
                                                            (
                                                                column: any,
                                                                index: number,
                                                            ) => {
                                                                return (
                                                                    <RiderTableCell
                                                                        key={`row${index}`}
                                                                        align={
                                                                            column?.align ??
                                                                            'left'
                                                                        }
                                                                        style={columnWidth.styleFor(
                                                                            column,
                                                                        )}
                                                                    >
                                                                        {getColumnComponentValue(
                                                                            row,
                                                                            column,
                                                                            null,
                                                                        )}
                                                                    </RiderTableCell>
                                                                );
                                                            },
                                                        )}
                                                    </TableRow>
                                                    <Divider
                                                        style={{
                                                            margin: '4px 0',
                                                            backgroundColor:
                                                                '#fff',
                                                        }}
                                                    />
                                                </React.Fragment>
                                            ),
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box className={styles.paginationContainer}>
                                <TablePagination
                                    rowsPerPageOptions={
                                        riderList?.lineItems?.length > 25 ||
                                        riderList?.totalCount > 25
                                            ? [25, 50, 75, 100]
                                            : []
                                    }
                                    component="div"
                                    count={riderList?.totalCount ?? 0}
                                    rowsPerPage={pagination?.pageSize}
                                    page={pagination?.pageNo}
                                    onChangePage={handleChangePage}
                                    onChangeRowsPerPage={
                                        handleChangeRowsPerPage
                                    }
                                />
                            </Box>
                        </>
                    ) : (
                        <Box className={styles.emptyState}>
                            <Typography className={styles.emptyTitle}>
                                {hasNoScopeAssigned
                                    ? 'No stores assigned'
                                    : 'No riders match this view'}
                            </Typography>
                            <Typography className={styles.emptySubtitle}>
                                {hasNoScopeAssigned
                                    ? 'Your account has no stores or zones assigned yet — contact your admin.'
                                    : activeFilterCount > 0 ||
                                        !!appliedSearchText
                                      ? 'Try clearing a filter or the search term.'
                                      : 'Nothing to show for the selected status.'}
                            </Typography>
                            {(activeFilterCount > 0 || !!appliedSearchText) && (
                                <Box mt={1} display="flex" style={{ gap: 8 }}>
                                    {!!appliedSearchText && (
                                        <Button
                                            className={styles.ghostButton}
                                            onClick={handleSearchClear}
                                        >
                                            Clear search
                                        </Button>
                                    )}
                                    {activeFilterCount > 0 && (
                                        <Button
                                            className={styles.ghostButton}
                                            onClick={handleClearAllFilters}
                                        >
                                            Clear filters
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>

                {!!isModalOpen && (
                    <AddEditStaffModal
                        open={isModalOpen}
                        isEdit={!!selectedRider}
                        defaultValue={selectedRider}
                        onClose={() => {
                            closeRiderModal?.();
                        }}
                    />
                )}

                {!!isViewModalOpen && (
                    <RiderViewModal
                        open={isViewModalOpen}
                        selectedRider={selectedRider}
                        onClose={() => {
                            closeViewModal?.();
                        }}
                    />
                )}

                {/* With governance on, deactivation needs a reason and becomes a request — the
                    plain confirm below only applies to the pre-governance direct flow. */}
                {!!deactivateConfirmRider && isBdDeactivationEnabled() && (
                    <RiderDeactivationModal
                        open
                        rider={deactivateConfirmRider}
                        onClose={() => setDeactivateConfirmRider(null)}
                        onSuccess={() => fetchRidersData?.()}
                    />
                )}

                {!!reactivateConfirmRider && (
                    <Dialog
                        open
                        onClose={() => setReactivateConfirmRider(null)}
                    >
                        <DialogTitle>Reactivate rider?</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                This raises a reactivation request for{' '}
                                {reactivateConfirmRider?.name}. They stay
                                deactivated until it is approved, then return to
                                the status they held before.
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => setReactivateConfirmRider(null)}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                variant="contained"
                                onClick={handleConfirmReactivate}
                            >
                                Submit request
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}

                {!!mpinRider && (
                    <ResetMpinModal
                        open
                        staff={{
                            id: mpinRider?.id,
                            name: mpinRider?.name,
                            contactNumber: mpinRider?.phone,
                        }}
                        onClose={() => setMpinRider(null)}
                    />
                )}

                {/* With governance on, deactivation needs a reason and becomes a request — the
                    plain confirm below only applies to the pre-governance direct flow. */}
                {!!deactivateConfirmRider && isBdDeactivationEnabled() && (
                    <RiderDeactivationModal
                        open
                        rider={deactivateConfirmRider}
                        onClose={() => setDeactivateConfirmRider(null)}
                        onSuccess={() => fetchRidersData?.()}
                    />
                )}

                <Dialog
                    open={
                        !!deactivateConfirmRider && !isBdDeactivationEnabled()
                    }
                    onClose={() => setDeactivateConfirmRider(null)}
                    PaperProps={{
                        style: {
                            borderRadius: 12,
                            padding: '8px 4px',
                            minWidth: 400,
                        },
                    }}
                >
                    <DialogTitle
                        disableTypography
                        style={{
                            padding: '16px 24px 8px',
                        }}
                    >
                        <Typography
                            style={{
                                fontFamily: 'Nunito',
                                fontWeight: 700,
                                fontSize: 18,
                                color: '#14112d',
                            }}
                        >
                            Deactivate Rider
                        </Typography>
                    </DialogTitle>
                    <DialogContent style={{ padding: '8px 24px 16px' }}>
                        <DialogContentText
                            style={{
                                fontFamily: 'Nunito',
                                fontWeight: 600,
                                fontSize: 16,
                                color: '#515f6d',
                                marginBottom: 0,
                            }}
                        >
                            Do you want to deactivate{' '}
                            <span style={{ fontWeight: 700, color: '#14112d' }}>
                                {deactivateConfirmRider?.name}
                            </span>
                            ? This will trigger the resignation for this user.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions style={{ padding: '8px 24px 16px', gap: 8 }}>
                        <Button
                            onClick={() => setDeactivateConfirmRider(null)}
                            style={{
                                fontFamily: 'Nunito',
                                fontWeight: 700,
                                color: '#43419e',
                                borderRadius: 8,
                                textTransform: 'none',
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmDeactivate}
                            variant="contained"
                            style={{
                                fontFamily: 'Nunito',
                                fontWeight: 700,
                                fontSize: 14,
                                backgroundColor: '#43419e',
                                color: '#fff',
                                borderRadius: 8,
                                textTransform: 'none',
                                padding: '6px 20px',
                            }}
                        >
                            Deactivate
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Spinner>
    );
};

export default RiderManagementPage;
